import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  FiPlus, FiEdit2, FiTrash2, FiUsers, FiX, FiCheck,
  FiUserCheck, FiUserX, FiLock, FiFilter,
} from 'react-icons/fi';
import { adminService } from '../../services/apiService';
import { useAuthStore } from '../../utils/store';
import { useT } from '../../utils/i18n';
import { FEATURES, isAlwaysOn, hasFeature, featureMap } from '../../utils/features';

const ADMIN_DAILY_LIMIT = 50;
const EMPTY_USER = {
  name: '', email: '', password: '', organization: '', role: 'editor', dailyBudget: 10,
  features: featureMap(null),
};

const Switch = ({ checked, onClick, disabled, title }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${checked ? 'bg-primary-600' : 'bg-gray-300'}`}
  >
    <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-4' : 'translate-x-0.5'}`} />
  </button>
);

// Per-user feature switches in the create/edit form. A feature the user's
// role always has (every feature for admins, Full Story for managers) shows
// as on and locked.
const FeatureAccessFields = ({ data, onChange }) => {
  const t = useT();
  const features = data.features || {};
  const allAlwaysOn = FEATURES.every(f => isAlwaysOn(f, data.role));
  return (
    <div className="md:col-span-2">
      <p className="block text-sm font-medium text-gray-700 mb-1">{t('admin.featureAccess')}</p>
      <p className="text-xs text-gray-400 mb-2">
        {allAlwaysOn ? t('admin.featuresAlwaysOnHint') : t('admin.featureAccessHint')}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {FEATURES.map((feature) => {
          const { key, icon: Icon, labelKey, descKey } = feature;
          const alwaysOn = isAlwaysOn(feature, data.role);
          const on = alwaysOn || features[key] === true;
          return (
            <div
              key={key}
              className={`flex items-start gap-3 rounded-lg border px-3 py-2.5 ${on ? 'border-primary-200 bg-primary-50' : 'border-gray-200'}`}
            >
              <Icon size={16} className={`mt-0.5 flex-shrink-0 ${on ? 'text-primary-600' : 'text-gray-400'}`} />
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-medium text-gray-900">{t(labelKey)}</span>
                <span className="block text-xs text-gray-500">
                  {alwaysOn && !allAlwaysOn ? t('admin.featureAlwaysOnForRole', { role: data.role }) : t(descKey)}
                </span>
              </span>
              <Switch
                checked={on}
                disabled={alwaysOn}
                onClick={() => onChange('features', { ...features, [key]: !on })}
                title={t(on ? 'admin.featureTurnOff' : 'admin.featureTurnOn', { feature: t(labelKey) })}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

const getBudgetColor = (pct) => {
  if (pct >= 100) return 'text-red-600 bg-red-50';
  if (pct >= 90)  return 'text-orange-600 bg-orange-50';
  if (pct >= 80)  return 'text-yellow-600 bg-yellow-50';
  return 'text-green-600 bg-green-50';
};

const UserForm = ({ data, onChange, isEdit, hideAdminOption }) => {
  const t = useT();
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.fullName')} *</label>
        <input
          type="text"
          value={data.name}
          onChange={e => onChange('name', e.target.value)}
          className="input w-full"
          placeholder="John Doe"
          required
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.email')} *</label>
        <input
          type="email"
          value={data.email}
          onChange={e => onChange('email', e.target.value)}
          className="input w-full"
          placeholder="user@example.com"
          required
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {isEdit ? t('admin.newPassword') : `${t('admin.password')} *`}
          {isEdit && <span className="text-gray-400 font-normal ml-1">{t('admin.leaveBlankPassword')}</span>}
        </label>
        <input
          type="password"
          value={data.password}
          onChange={e => onChange('password', e.target.value)}
          className="input w-full"
          placeholder="Min. 6 characters"
          minLength={isEdit ? undefined : 6}
          required={!isEdit}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.organization')}</label>
        <input
          type="text"
          value={data.organization || ''}
          onChange={e => onChange('organization', e.target.value)}
          className="input w-full"
          placeholder={t('admin.orgPlaceholder')}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.role')}</label>
        <select
          value={data.role}
          onChange={e => onChange('role', e.target.value)}
          className="input w-full"
        >
          <option value="editor">{t('admin.editor')}</option>
          <option value="manager">{t('admin.manager')}</option>
          {/* Managers can't grant admin — hidden here as the first line of
              defense; AuthService rejects it server-side regardless if this
              were ever bypassed (e.g. a stale form still holding 'admin'). */}
          {!hideAdminOption && <option value="admin">{t('admin.admin')}</option>}
        </select>
      </div>
      <div className="md:col-span-2">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('admin.dailyTokens')}{' '}
          {data.role === 'admin'
            ? <span className="text-gray-400 font-normal">{t('admin.fixedForAdmins')}</span>
            : <span className="font-bold text-primary-600">${data.dailyBudget}/day</span>
          }
        </label>
        <input
          type="range"
          min="1"
          max="50"
          value={data.role === 'admin' ? 50 : data.dailyBudget}
          onChange={e => onChange('dailyBudget', Number(e.target.value))}
          className="w-full accent-primary-600"
          disabled={data.role === 'admin'}
        />
        <div className="flex justify-between text-xs text-gray-400 mt-1">
          <span>$1</span><span>$10</span><span>$25</span><span>$50</span>
        </div>
        <p className="text-xs text-gray-400 mt-1">{t('admin.budgetHint')}</p>
      </div>
      <FeatureAccessFields data={data} onChange={onChange} />
    </div>
  );
};

export const UserManagement = () => {
  const t = useT();
  const { user: currentUser } = useAuthStore();
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'superadmin';
  const isManager = currentUser?.role === 'manager';

  const [users, setUsers]             = useState([]);
  const [isLoading, setIsLoading]     = useState(false);
  const [showCreate, setShowCreate]   = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deletingId, setDeletingId]   = useState(null);
  const [newUser, setNewUser]         = useState({ ...EMPTY_USER });
  const [featureFilter, setFeatureFilter] = useState('');   // '' | '<key>:on' | '<key>:off'
  const [pendingToggle, setPendingToggle] = useState(null); // '<userId>:<key>' while saving

  if (!isAdmin && !isManager) {
    return (
      <div className="flex flex-col items-center justify-center text-center h-64 max-w-md mx-auto gap-3">
        <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
          <FiLock size={20} className="text-gray-400" />
        </div>
        <h1 className="text-lg font-semibold text-gray-900">Access Restricted</h1>
        <p className="text-sm text-gray-500">User Management is not available for your role.</p>
      </div>
    );
  }

  const loadUsers = async () => {
    setIsLoading(true);
    try {
      setUsers(await adminService.listUsers());
    } catch {
      toast.error(t('admin.failedLoadUsers'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { loadUsers(); }, []);

  // ── Create ────────────────────────────────────────────────────────────────
  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await adminService.createUser(newUser);
      toast.success(t('admin.userCreated', { email: newUser.email }));
      setShowCreate(false);
      setNewUser({ ...EMPTY_USER });
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.message || t('admin.failedCreateUser'));
    }
  };

  // ── Edit ─────────────────────────────────────────────────────────────────
  const handleEditSave = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...editingUser };
      if (!payload.password) delete payload.password;
      await adminService.updateUser(editingUser._id, payload);
      toast.success(t('admin.userUpdated'));
      if (editingUser._id === currentUser?._id && payload.features) {
        const flags = Object.fromEntries(FEATURES.map(f => [f.field, payload.features[f.key] === true]));
        useAuthStore.setState(s => ({ user: s.user && { ...s.user, ...flags } }));
      }
      setEditingUser(null);
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.message || t('admin.failedUpdateUser'));
    }
  };

  // ── Toggle active ─────────────────────────────────────────────────────────
  const handleToggleActive = async (u) => {
    try {
      await adminService.setUserActive(u._id, !u.isActive);
      toast.success(u.isActive ? t('admin.deactivate') + ' done' : t('admin.activate') + ' done');
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update status');
    }
  };

  // ── Toggle one feature for one user ──────────────────────────────────────
  // Updates just that row instead of reloading the list (which recomputes
  // every user's spend).
  const handleToggleFeature = async (u, feature) => {
    const enabled = u[feature.field] !== true;
    const pendingKey = `${u._id}:${feature.key}`;
    setPendingToggle(pendingKey);
    try {
      await adminService.setUserFeature(u._id, feature.key, enabled);
      setUsers(prev => prev.map(x => (x._id === u._id ? { ...x, [feature.field]: enabled } : x)));
      // A manager switching their own access: show it in the sidebar now.
      if (u._id === currentUser?._id) {
        useAuthStore.setState(s => ({ user: s.user && { ...s.user, [feature.field]: enabled } }));
      }
      const params = { feature: t(feature.labelKey), name: u.name };
      toast.success(enabled ? t('admin.featureEnabled', params) : t('admin.featureDisabled', params));
    } catch (err) {
      toast.error(err.response?.data?.message || t('admin.failedUpdateUser'));
    } finally {
      setPendingToggle(p => (p === pendingKey ? null : p));
    }
  };

  const visibleUsers = users.filter(u => {
    if (!featureFilter) return true;
    const [key, state] = featureFilter.split(':');
    return hasFeature(u, key) === (state === 'on');
  });

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = async (u) => {
    if (!window.confirm(t('admin.confirmDelete', { name: u.name }))) return;
    setDeletingId(u._id);
    try {
      await adminService.deleteUser(u._id);
      toast.success(t('admin.userDeleted'));
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.message || t('admin.failedDeleteUser'));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <FiUsers size={22} className="text-primary-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('admin.userManagement')}</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              {users.length} user{users.length !== 1 ? 's' : ''} · manage accounts, roles, and daily token budgets
            </p>
          </div>
        </div>
        <button
          onClick={() => { setShowCreate(v => !v); setEditingUser(null); }}
          className="btn btn-primary flex items-center gap-2"
        >
          <FiPlus size={16} />
          {t('admin.createUser')}
        </button>
      </div>

      {/* Create Form */}
      {showCreate && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">{t('admin.newUser')}</h2>
            <button onClick={() => setShowCreate(false)} className="text-gray-400 hover:text-gray-600">
              <FiX size={20} />
            </button>
          </div>
          <form onSubmit={handleCreate} className="space-y-4">
            <UserForm data={newUser} onChange={(k, v) => setNewUser(p => ({ ...p, [k]: v }))} isEdit={false} hideAdminOption={isManager} />
            <div className="flex gap-3 justify-end pt-2 border-t border-gray-100">
              <button type="button" onClick={() => setShowCreate(false)} className="btn btn-ghost">{t('admin.cancel')}</button>
              <button type="submit" className="btn btn-primary">
                <FiPlus size={15} />{t('admin.createUser')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Edit Modal */}
      {editingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {t('admin.editUser')} {editingUser.name}
              </h2>
              <button onClick={() => setEditingUser(null)} className="text-gray-400 hover:text-gray-600">
                <FiX size={20} />
              </button>
            </div>
            <form onSubmit={handleEditSave} className="p-6 space-y-4">
              <UserForm
                data={editingUser}
                onChange={(k, v) => setEditingUser(p => ({ ...p, [k]: v }))}
                isEdit={true}
                hideAdminOption={isManager}
              />
              <div className="flex gap-3 justify-end pt-2 border-t border-gray-100">
                <button type="button" onClick={() => setEditingUser(null)} className="btn btn-ghost">{t('admin.cancel')}</button>
                <button type="submit" className="btn btn-primary">
                  <FiCheck size={15} />{t('admin.saveChanges')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Feature access summary + filter */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-gray-700 mr-1">{t('admin.featureAccess')}:</span>
        {FEATURES.map(f => {
          const Icon = f.icon;
          const count = users.filter(u => hasFeature(u, f.key)).length;
          const active = featureFilter === `${f.key}:on`;
          return (
            <button
              key={f.key}
              type="button"
              onClick={() => setFeatureFilter(active ? '' : `${f.key}:on`)}
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${active ? 'border-primary-600 bg-primary-600 text-white' : 'border-gray-200 bg-white text-gray-700 hover:border-primary-300'}`}
              title={t('admin.filterHasFeature', { feature: t(f.labelKey) })}
            >
              <Icon size={12} />
              {t(f.labelKey)}
              <span className={active ? 'text-white' : 'text-gray-400'}>{count}/{users.length}</span>
            </button>
          );
        })}
        <div className="flex items-center gap-1.5 ml-auto">
          <FiFilter size={14} className="text-gray-400" />
          <select
            value={featureFilter}
            onChange={e => setFeatureFilter(e.target.value)}
            className="input py-1 text-sm w-auto"
            aria-label={t('admin.filterByFeature')}
          >
            <option value="">{t('admin.filterAllUsers')}</option>
            {FEATURES.map(f => (
              <React.Fragment key={f.key}>
                <option value={`${f.key}:on`}>{t('admin.filterHasFeature', { feature: t(f.labelKey) })}</option>
                <option value={`${f.key}:off`}>{t('admin.filterLacksFeature', { feature: t(f.labelKey) })}</option>
              </React.Fragment>
            ))}
          </select>
        </div>
      </div>

      {/* Users Table */}
      <div className="card p-0 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">{t('admin.loadingUsers')}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[780px]">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">{t('admin.user')}</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">{t('admin.role')}</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Status</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">{t('admin.todaySpend')}</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">{t('admin.dailyLimit')}</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">{t('admin.remaining')}</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">{t('admin.featureAccess')}</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">{t('admin.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {visibleUsers.map(u => {
                  const limit = u.role === 'admin' ? ADMIN_DAILY_LIMIT : (u.dailyLimit ?? u.dailyBudget ?? 10);
                  const pct   = limit > 0 ? Math.min(100, (u.todaySpend / limit) * 100) : 0;
                  const isSelf = u._id === currentUser?._id;
                  // Belt-and-suspenders: the backend already excludes admin/
                  // superadmin accounts from a manager's list entirely (see
                  // AuthService.listUsers), so this normally never matters —
                  // but if an admin row ever did show up here, a manager
                  // still shouldn't get action buttons for it.
                  const targetIsAdmin = u.role === 'admin' || u.role === 'superadmin';
                  const canActOnTarget = !isManager || !targetIsAdmin;
                  return (
                    <tr key={u._id} className={`hover:bg-gray-50 ${!u.isActive ? 'opacity-60' : ''}`}>
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900">{u.name}</p>
                        <p className="text-gray-500 text-xs">{u.email}</p>
                        {u.organization && <p className="text-gray-400 text-xs">{u.organization}</p>}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`badge text-xs ${u.role === 'admin' ? 'badge-primary' : 'badge-warning'}`}>
                          {u.role}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${u.isActive !== false ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {u.isActive !== false ? t('admin.statusActive') : t('admin.statusInactive')}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${getBudgetColor(pct)}`}>
                          ${u.todaySpend.toFixed(4)}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-medium">
                        {u.role === 'admin'
                          ? <span className="text-gray-400 text-xs">${ADMIN_DAILY_LIMIT} ({t('admin.fixed')})</span>
                          : <span>${limit}</span>
                        }
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-16 bg-gray-200 rounded-full h-1.5">
                            <div
                              className={`h-1.5 rounded-full ${pct >= 100 ? 'bg-red-500' : pct >= 80 ? 'bg-yellow-500' : 'bg-green-500'}`}
                              style={{ width: `${Math.min(100, pct)}%` }}
                            />
                          </div>
                          <span className="text-xs text-gray-600">${(u.remainingBudget ?? 0).toFixed(2)}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {targetIsAdmin ? (
                          <span className="text-gray-400 text-xs" title={t('admin.featuresAlwaysOnHint')}>{t('admin.allFeatures')}</span>
                        ) : (
                          <div className="flex flex-col gap-1.5">
                            {FEATURES.map(f => {
                              const label = t(f.labelKey);
                              // e.g. Full Story for a manager: always on, nothing to switch.
                              if (isAlwaysOn(f, u.role)) {
                                return (
                                  <div key={f.key} className="flex items-center justify-between gap-2 min-w-[130px]">
                                    <span className="text-xs text-gray-800 font-medium">{label}</span>
                                    <span className="text-[11px] text-gray-400" title={t('admin.featureAlwaysOnForRole', { role: u.role })}>{t('admin.mediaAIAlwaysOn')}</span>
                                  </div>
                                );
                              }
                              const on = u[f.field] === true;
                              return (
                                <div key={f.key} className="flex items-center justify-between gap-2 min-w-[130px]">
                                  <span className={`text-xs ${on ? 'text-gray-800 font-medium' : 'text-gray-400'}`}>{label}</span>
                                  <Switch
                                    checked={on}
                                    disabled={!canActOnTarget || pendingToggle === `${u._id}:${f.key}`}
                                    onClick={() => handleToggleFeature(u, f)}
                                    title={t(on ? 'admin.featureTurnOff' : 'admin.featureTurnOn', { feature: label })}
                                  />
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          {canActOnTarget && (
                            <button
                              onClick={() => { setEditingUser({ ...u, password: '', features: featureMap(u) }); setShowCreate(false); }}
                              className="btn btn-ghost btn-sm"
                              title={t('admin.edit')}
                            >
                              <FiEdit2 size={13} />
                            </button>
                          )}
                          {!isSelf && canActOnTarget && (
                            <button
                              onClick={() => handleToggleActive(u)}
                              className="btn btn-ghost btn-sm"
                              title={u.isActive !== false ? t('admin.deactivate') : t('admin.activate')}
                            >
                              {u.isActive !== false
                                ? <FiUserX size={13} className="text-yellow-600" />
                                : <FiUserCheck size={13} className="text-green-600" />
                              }
                            </button>
                          )}
                          {!isSelf && canActOnTarget && (
                            <button
                              onClick={() => handleDelete(u)}
                              disabled={deletingId === u._id}
                              className="btn btn-ghost btn-sm text-danger-600 hover:bg-danger-50"
                              title={t('admin.deleteUser')}
                            >
                              <FiTrash2 size={13} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {visibleUsers.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-gray-400">
                      {users.length > 0 ? t('admin.noUsersMatchFilter') : t('admin.noUsersFound')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
